import { registerMockAPI } from '~/mocks/mockApis';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import { deliveryAgencyKeys } from './keys';

import type { TypeGetOrdersParams, TypeGetOrdersResponse } from '~/types/delivery-agency.types';

/**
 * [배송 주문 관리] 배송 주문 리스트 조회 API
 */
export const fetchOrders = async (params: TypeGetOrdersParams): Promise<TypeGetOrdersResponse> => {
  const { data } = await axiosInstance.get<TypeGetOrdersResponse>(API_ENDPOINT.DELIVERY_AGENCY.GET_ORDERS, { params });
  return data;
};

type UseGetOrdersQueryOptions = {
  params: TypeGetOrdersParams;
  enabled?: boolean;
};

/**
 * [배송 주문 관리] 배송 주문 리스트 조회 Query Hook
 */
export const useGetOrdersQuery = ({ params, enabled = true }: UseGetOrdersQueryOptions) => {
  return useQuery({
    queryKey: deliveryAgencyKeys.orders(params),
    queryFn: () => fetchOrders(params),
    enabled,
    staleTime: 0,
    gcTime: 1000 * 30,
    placeholderData: keepPreviousData,
  });
};

/** 배송예정일은 기본 검색 조건(내일, 2026-05-20)과 맞춘다. */
const ordersFixture: TypeGetOrdersResponse['data'] = [
  {
    orderId: 1001,
    orderStatus: { code: 'OUTBOUND_READY', description: '출고 대기' },
    statusUpdatedAt: '2026-05-19T08:10:00',
    clientOrderCode: 'ORD-20260519-0001',
    outboundCode: 'OB20260519001',
    estimatedDeliveryDate: '2026-05-20',
    deliveryType: { code: 'DAWN', description: '샛별배송' },
    courierCode: { code: 'NM', description: '넥스트마일' },
    receiverName: '홍길동',
    ordererName: '홍길동',
    productName: '유기농 우유 900ml 외 2건',
    createdAt: '2026-05-19T08:00:00',
  },
  {
    orderId: 1002,
    orderStatus: { code: 'OUTBOUND_PROCESSING', description: '출고 진행중' },
    statusUpdatedAt: '2026-05-19T08:11:00',
    clientOrderCode: 'ORD-20260519-0002',
    outboundCode: 'OB20260519002',
    estimatedDeliveryDate: '2026-05-20',
    deliveryType: { code: 'DAWN', description: '샛별배송' },
    courierCode: { code: 'NM', description: '넥스트마일' },
    receiverName: '김수령',
    ordererName: '김주문',
    productName: '제철 사과 5kg',
    createdAt: '2026-05-19T08:01:00',
  },
  {
    orderId: 1003,
    orderStatus: { code: 'ON_THE_WAY', description: '배송 중' },
    statusUpdatedAt: '2026-05-19T08:12:00',
    clientOrderCode: 'ORD-20260519-0003',
    outboundCode: 'OB20260519003',
    estimatedDeliveryDate: '2026-05-20',
    deliveryType: { code: 'DAY', description: '하루배송' },
    courierCode: { code: 'NM', description: '넥스트마일' },
    receiverName: '이배송',
    ordererName: '이주문',
    productName: '탄산수 500ml x24',
    createdAt: '2026-05-19T08:02:00',
  },
  {
    orderId: 1004,
    orderStatus: { code: 'DELIVERY_COMPLETED', description: '배송 완료' },
    statusUpdatedAt: '2026-05-19T08:13:00',
    clientOrderCode: 'ORD-20260519-0004',
    outboundCode: 'OB20260519004',
    estimatedDeliveryDate: '2026-05-20',
    deliveryType: { code: 'DAWN', description: '샛별배송' },
    courierCode: { code: 'NM', description: '넥스트마일' },
    receiverName: '박완료',
    ordererName: '박주문',
    productName: '샐러드 도시락 세트',
    createdAt: '2026-05-19T08:03:00',
  },
  {
    orderId: 1005,
    orderStatus: { code: 'CANCELED', description: '주문 취소' },
    statusUpdatedAt: '2026-05-19T08:14:00',
    clientOrderCode: 'ORD-20260519-0005',
    outboundCode: 'OB20260519005',
    estimatedDeliveryDate: '2026-05-20',
    deliveryType: { code: 'DAWN', description: '샛별배송' },
    courierCode: { code: 'NM', description: '넥스트마일' },
    receiverName: '정취소',
    ordererName: '정주문',
    productName: '냉동 만두 1.4kg',
    createdAt: '2026-05-19T08:04:00',
  },
  {
    orderId: 1006,
    orderStatus: { code: 'DELIVERY_FAILED', description: '배송 실패' },
    statusUpdatedAt: '2026-05-19T08:15:00',
    clientOrderCode: 'ORD-20260519-0006',
    outboundCode: 'OB20260519006',
    estimatedDeliveryDate: '2026-05-20',
    deliveryType: { code: 'DAY', description: '하루배송' },
    courierCode: { code: 'NM', description: '넥스트마일' },
    receiverName: '최실패',
    ordererName: '최주문',
    productName: '그릭 요거트 450g',
    createdAt: '2026-05-19T08:05:00',
  },
];

/**
 * [배송 주문 관리] 배송 주문 리스트 조회 Mock
 *
 * @remarks
 * DevTools에서 Mock을 활성화하면 응답합니다.
 */
registerMockAPI<TypeGetOrdersResponse>({
  method: 'get',
  endpoint: '/api-delivery-agency/orders',
  label: '배송 주문 관리/배송 주문 리스트',
  scenarios: [
    {
      name: '정상 응답',
      response: {
        data: ordersFixture,
        pagination: { cursor: 1, listView: 50, listTotal: ordersFixture.length },
      },
    },
    {
      name: '데이터 없음',
      response: {
        data: [],
        pagination: { cursor: 1, listView: 50, listTotal: 0 },
      },
    },
  ],
});
